import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ProgressHeader, PlannerDataBadge } from '../settlement';
import { PlannerDataList, PlannerDataSection } from '../settlement/PlannerDataSection';
import { useSettlementSession } from '../../hooks/useSettlementSession';
import type { VehicleTaskDefinition, VehicleTaskKind, VehicleFormRow } from './vehicleTaskConfig';
import { VEHICLE_TASK_ORDER, VEHICLE_TASK_DEFINITIONS } from './vehicleTaskConfig';
import { emptyVehicleRow, extractPlannerVehicleRows, mergeRowsWithSaved } from './vehiclePlannerUtils';

const IAD_PHASE = 'iad';
const IAD_STEP_ID = 'iad-3';

function hubPath(estateId: string | undefined) {
  return estateId ? `/sframe-137/${estateId}` : '/sframe-137';
}

function taskPath(route: string, estateId: string | undefined) {
  return estateId ? `${route}/${estateId}` : route;
}

interface VehicleTaskFormProps {
  taskKind: VehicleTaskKind;
}

export default function VehicleTaskForm({ taskKind }: VehicleTaskFormProps) {
  const def: VehicleTaskDefinition = VEHICLE_TASK_DEFINITIONS[taskKind];
  const { estateId: paramEstateId } = useParams<{ estateId: string }>();
  const estateId = paramEstateId || null;
  const navigate = useNavigate();

  const { loadFrameData, saveFrameData, loadPlannerData, loadPhaseProgress, toggleChecklistItem, isLoading, error } =
    useSettlementSession(estateId);

  const [rows, setRows] = useState<VehicleFormRow[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [completedChecklistIds, setCompletedChecklistIds] = useState<Set<string>>(new Set());

  const progressTotal = VEHICLE_TASK_ORDER.length;
  const completedTasks = VEHICLE_TASK_ORDER.filter((k) =>
    completedChecklistIds.has(VEHICLE_TASK_DEFINITIONS[k].checklistItemId)
  ).length;

  const refreshChecklist = useCallback(async () => {
    if (!estateId) return;
    const steps = await loadPhaseProgress(IAD_PHASE);
    const step = steps.find((s) => s.stepId === IAD_STEP_ID);
    setCompletedChecklistIds(new Set(step?.completedChecklistItems ?? []));
  }, [estateId, loadPhaseProgress]);

  useEffect(() => {
    void refreshChecklist();
  }, [refreshChecklist]);

  useEffect(() => {
    if (!estateId) {
      setLoaded(true);
      setRows([emptyVehicleRow()]);
      return;
    }

    let cancelled = false;

    const load = async () => {
      const [frameRes, planner] = await Promise.all([
        loadFrameData(def.frameId),
        loadPlannerData('frame-193'),
      ]);
      if (cancelled) return;

      const savedRows = (frameRes?.data?.vehicles as VehicleFormRow[] | undefined) ?? undefined;
      const seed = extractPlannerVehicleRows(planner ?? undefined);
      setRows(mergeRowsWithSaved(savedRows, seed));
      setLoaded(true);
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [estateId, def.frameId, loadFrameData, loadPlannerData]);

  const updateRow = (id: string, patch: Partial<VehicleFormRow>) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const updateRowValue = (id: string, key: string, value: string) => {
    setRows((prev) =>
      prev.map((r) =>
        r.id === id
          ? {
              ...r,
              values: { ...r.values, [key]: value },
            }
          : r
      )
    );
  };

  const addVehicle = () => {
    setRows((prev) => [...prev, emptyVehicleRow()]);
  };

  const removeVehicle = (id: string) => {
    setRows((prev) => (prev.length <= 1 ? prev : prev.filter((r) => r.id !== id)));
  };

  const handleSave = async (): Promise<boolean> => {
    setSaveMessage(null);
    if (!estateId) {
      setSaveMessage('Select an estate from the dashboard so changes can be saved.');
      return false;
    }
    const ok = await saveFrameData(def.frameId, { vehicles: rows });
    setSaveMessage(ok ? 'Saved.' : 'Could not save. Try again.');
    return ok;
  };

  const handleMarkTaskComplete = async () => {
    if (!estateId) return;
    const ok = await handleSave();
    if (!ok) return;
    await toggleChecklistItem(IAD_STEP_ID, IAD_PHASE, def.checklistItemId, true);
    await refreshChecklist();
    setSaveMessage('Task marked complete for this checklist item.');
  };

  if (!loaded) {
    return (
      <div className="max-w-3xl mx-auto py-12 text-grey-600 text-sm">Loading vehicle task…</div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto pb-12">
      {error && (
        <div className="mb-4 bg-orange-100 border border-orange-300 rounded-md p-3 text-orange-900 text-sm">{error}</div>
      )}

      <div className="flex items-center gap-3 mb-6">
        <button
          type="button"
          onClick={() => navigate(hubPath(estateId ?? undefined))}
          className="w-[35px] h-[35px] flex items-center justify-center text-grey-600 hover:text-blue-900 transition-colors"
          aria-label="Go back"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h1 className="font-display text-[28px] leading-tight text-blue-900">{def.title}</h1>
      </div>

      <p className="text-sm text-grey-600 mb-6">{def.description}</p>

      <ProgressHeader
        title="Vehicle tasks"
        subtitle="Work through each task for every vehicle in the estate."
        completedSteps={completedTasks}
        totalSteps={progressTotal}
      />

      <div className="flex flex-wrap gap-2 mb-8">
        {VEHICLE_TASK_ORDER.map((k) => {
          const d = VEHICLE_TASK_DEFINITIONS[k];
          const active = k === taskKind;
          return (
            <Link
              key={k}
              to={taskPath(d.route, estateId ?? undefined)}
              className={`text-xs font-medium px-3 py-1.5 rounded-md border ${
                active ? 'bg-blue-600 text-white-alt border-blue-600' : 'border-grey-300 text-blue-900 hover:border-blue-400'
              }`}
            >
              {d.title}
            </Link>
          );
        })}
      </div>

      {estateId && (
        <div className="mb-6">
          <PlannerDataSection
            plannerFrameId="frame-193"
            estateId={estateId}
            title="Vehicles from estate plan (frame-193)"
            emptyMessage="No vehicles were captured in the estate plan."
            initiallyExpanded
            renderData={(data) => (
              <PlannerDataList
                items={extractPlannerVehicleRows(data)}
                emptyMessage="No vehicle list found in planner export."
                renderItem={(item) => (
                  <div className="text-sm text-gray-900">
                    <span className="font-medium">
                      {[item.year, item.make, item.model].filter(Boolean).join(' ') || 'Vehicle'}
                    </span>
                    {item.vin ? <span className="text-gray-600"> · VIN {item.vin}</span> : null}
                    {item.licensePlate ? <span className="text-gray-600"> · Plate {item.licensePlate}</span> : null}
                  </div>
                )}
              />
            )}
          />
        </div>
      )}

      {!estateId && (
        <div className="mb-6 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-950">
          <PlannerDataBadge variant="from-planning" />
          <span>Open this page from an estate URL (with estate ID) to load planner vehicles and save executor notes.</span>
        </div>
      )}

      <div className="space-y-6 mb-8">
        {rows.map((row, index) => (
          <div key={row.id} className="rounded-xl border border-grey-300 bg-white-alt p-5">
            <div className="flex items-center justify-between gap-2 mb-4">
              <h2 className="text-base font-semibold text-blue-900">Vehicle {index + 1}</h2>
              <button
                type="button"
                onClick={() => removeVehicle(row.id)}
                className="text-xs text-red-700 hover:underline disabled:opacity-40"
                disabled={rows.length <= 1}
              >
                Remove
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
              <label className="text-xs font-semibold text-black">
                Year
                <input
                  className="mt-1 w-full rounded-lg border border-grey-300 px-3 py-2 text-sm"
                  value={row.year}
                  onChange={(e) => updateRow(row.id, { year: e.target.value })}
                />
              </label>
              <label className="text-xs font-semibold text-black">
                Make
                <input
                  className="mt-1 w-full rounded-lg border border-grey-300 px-3 py-2 text-sm"
                  value={row.make}
                  onChange={(e) => updateRow(row.id, { make: e.target.value })}
                />
              </label>
              <label className="text-xs font-semibold text-black">
                Model
                <input
                  className="mt-1 w-full rounded-lg border border-grey-300 px-3 py-2 text-sm"
                  value={row.model}
                  onChange={(e) => updateRow(row.id, { model: e.target.value })}
                />
              </label>
              <label className="text-xs font-semibold text-black">
                VIN
                <input
                  className="mt-1 w-full rounded-lg border border-grey-300 px-3 py-2 text-sm"
                  value={row.vin}
                  onChange={(e) => updateRow(row.id, { vin: e.target.value })}
                />
              </label>
              <label className="text-xs font-semibold text-black sm:col-span-2">
                License plate
                <input
                  className="mt-1 w-full rounded-lg border border-grey-300 px-3 py-2 text-sm"
                  value={row.licensePlate}
                  onChange={(e) => updateRow(row.id, { licensePlate: e.target.value })}
                />
              </label>
            </div>

            <div className="space-y-3">
              {def.fields.map((field) => (
                <label key={field.key} className="block text-xs font-semibold text-black">
                  {field.label}
                  {field.type === 'textarea' ? (
                    <textarea
                      className="mt-1 w-full rounded-lg border border-grey-300 px-3 py-2 text-sm min-h-[72px]"
                      value={row.values[field.key] ?? ''}
                      onChange={(e) => updateRowValue(row.id, field.key, e.target.value)}
                    />
                  ) : field.type === 'select' ? (
                    <select
                      className="mt-1 w-full rounded-lg border border-grey-300 px-3 py-2 text-sm bg-white"
                      value={row.values[field.key] ?? ''}
                      onChange={(e) => updateRowValue(row.id, field.key, e.target.value)}
                    >
                      {(field.options ?? [{ value: '', label: '—' }]).map((opt) => (
                        <option key={opt.value || 'empty'} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      className="mt-1 w-full rounded-lg border border-grey-300 px-3 py-2 text-sm"
                      value={row.values[field.key] ?? ''}
                      onChange={(e) => updateRowValue(row.id, field.key, e.target.value)}
                    />
                  )}
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={() => addVehicle()}
        className="mb-4 text-sm font-medium text-blue-700 hover:underline"
      >
        + Add another vehicle
      </button>

      <div className="flex flex-wrap gap-3 items-center">
        <button
          type="button"
          onClick={() => void handleSave()}
          className="rounded-lg bg-blue-600 text-white-alt px-5 py-2.5 text-sm font-semibold hover:bg-blue-700"
        >
          Save
        </button>
        <button
          type="button"
          onClick={() => void handleMarkTaskComplete()}
          className="rounded-lg border border-blue-600 text-blue-900 px-5 py-2.5 text-sm font-semibold hover:bg-blue-50"
        >
          Save & mark this task complete
        </button>
        {saveMessage && <span className="text-sm text-grey-700">{saveMessage}</span>}
        {isLoading && <span className="text-sm text-grey-600">Saving…</span>}
      </div>
    </div>
  );
}
